import React from 'react';
import {
  Bookmark,
  Briefcase,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  IndianRupee,
  Layers,
  Rocket,
  UserPlus,
  Users,
  X,
  MapPin,
  Clock,
  Building2,
} from 'lucide-react';
import {
  companyInitials,
  formatExperience,
  formatPostedAgo,
  jobApplyKey,
} from './jobCardUtils';
import { appDesignTokens as tokens } from './appDesignTokens';
import { useTheme } from '../context/ThemeContext';

const JobCardActions = ({ job, onApply, onFindContact, onViewDetails, green, isDark, border, cardBg, muted, accentColor }) => (
  <div className="job-feed-card-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
    {job.job_url && (
      <a
        href={job.job_url}
        target="_blank"
        rel="noreferrer"
        onClick={() => onApply?.(job)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          borderRadius: tokens.radius.md,
          background: isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : tokens.colors.accent,
          color: isDark ? '#1a1612' : '#fff',
          fontWeight: 700, fontSize: 12, textDecoration: 'none',
        }}
      >
        <Rocket size={14} /> Apply
      </a>
    )}
    <button
      type="button"
      onClick={onFindContact}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: tokens.radius.md,
        border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)'}`,
        background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)',
        color: accentColor,
        fontWeight: 600, fontSize: 12, cursor: 'pointer',
      }}
    >
      <UserPlus size={14} /> Find contact
    </button>
    <button
      type="button"
      onClick={onViewDetails}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: tokens.radius.md,
        border: `1px solid ${border}`,
        background: isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surface,
        color: muted,
        fontWeight: 600, fontSize: 12, cursor: 'pointer',
      }}
    >
      <FileText size={14} /> Details
    </button>
  </div>
);

const JobFeedCard = ({
  job,
  isExpanded = false,
  onToggleExpand,
  onToggleSaved,
  onReject,
  onApply,
  onFindContact,
  onViewDetails,
  showSave = true,
  showDismiss = true,
  cardBg: cardBgProp,
  border: borderProp,
  text: textProp,
  muted: mutedProp,
  green: greenProp,
}) => {
  const { isDark } = useTheme();

  // Use props if provided (from parent page), otherwise derive from theme
  const cardBg = cardBgProp ?? (isDark ? 'rgba(255,255,255,0.04)' : tokens.colors.surface);
  const border = borderProp ?? (isDark ? 'rgba(255,255,255,0.08)' : tokens.colors.border);
  const text = textProp ?? (isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.textPrimary);
  const muted = mutedProp ?? (isDark ? 'rgba(255,255,255,0.45)' : tokens.colors.textMuted);
  const green = greenProp ?? (isDark ? '#34d399' : tokens.colors.success);
  const accentColor = isDark ? '#F59E0B' : tokens.colors.accent;
  const textSecondary = isDark ? 'rgba(255,255,255,0.65)' : tokens.colors.textSecondary;
  const tagBg = isDark ? 'rgba(255,255,255,0.06)' : tokens.colors.surfaceInset;
  const reasonsBg = isDark ? 'rgba(255,255,255,0.03)' : tokens.colors.surfaceInset;
  const logoBg = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)';
  const logoBorder = isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.15)';
  const matchBadgeBg = isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)';
  const matchBadgeBorder = isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)';
  const salaryTagBg = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)';
  const salaryTagBorder = isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.12)';
  const dimText = isDark ? 'rgba(255,255,255,0.28)' : tokens.colors.textDim;
  const cardBorderColor = job.is_match
    ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.22)')
    : border;

  const key = jobApplyKey(job) || `${job.company_name}-${job.role}`;
  const isSaved = job.user_status === 'saved';
  const rawReasons = job.reasons || [];
  const allWhyReasons = rawReasons.filter((r) => r.reason_type !== 'alumni_inside');
  const maxReasons = 2;
  const whyReasons = allWhyReasons.slice(0, maxReasons);
  const remainingCount = allWhyReasons.length - maxReasons;

  return (
    <article
      className="job-feed-card"
      style={{
        background: cardBg,
        border: `1px solid ${cardBorderColor}`,
        borderRadius: tokens.radius.card,
        padding: '14px 16px',
        marginBottom: 12,
        boxShadow: isDark ? 'none' : tokens.shadow.card,
        fontFamily: tokens.fonts.body,
      }}
    >
      <div className="job-feed-card-layout" style={{ display: 'flex', gap: 12 }}>
        <div
          className="job-feed-card-logo"
          style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: logoBg, color: accentColor,
            border: `1px solid ${logoBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14,
          }}
        >
          {companyInitials(job.company_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="job-feed-card-head" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <div className="job-feed-card-copy" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: text, lineHeight: 1.24 }}>{job.role}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: textSecondary, fontSize: 13, fontWeight: 500 }}>
                  <Building2 size={13} color={muted} />
                  {job.company_name}
                </div>
                {job.location && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: textSecondary, fontSize: 13, fontWeight: 500 }}>
                    <MapPin size={13} color={muted} />
                    {job.location}
                  </div>
                )}
                {formatPostedAgo(job.posted_at) && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: muted, fontSize: 11.5, fontWeight: 500 }}>
                    <Clock size={13} color={muted} />
                    {formatPostedAgo(job.posted_at)}
                  </div>
                )}
                {job.job_function && job.job_function !== 'Not Applicable' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: muted, fontSize: 11.5, fontWeight: 500 }}>
                    <Layers size={13} color={muted} />
                    {job.job_function}
                  </div>
                )}
              </div>
            </div>
            <div className="job-feed-card-tools" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              {job.is_match && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                  borderRadius: tokens.radius.pill,
                  background: matchBadgeBg,
                  border: `1px solid ${matchBadgeBorder}`,
                  fontSize: 10.5, fontWeight: 700, color: accentColor, whiteSpace: 'nowrap',
                }}>
                  <Check size={12} /> Matched
                </span>
              )}
              {showSave && onToggleSaved && (
                <button type="button" onClick={() => onToggleSaved(job)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }} aria-label="Save job">
                  <Bookmark size={18} color={isSaved ? green : muted} fill={isSaved ? green : 'none'} />
                </button>
              )}
              {showDismiss && onReject && (
                <button
                  type="button"
                  onClick={() => onReject(job)}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : tokens.colors.surfaceInset}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  aria-label="Dismiss job"
                >
                  <X size={14} color={muted} />
                </button>
              )}
            </div>
          </div>

          {/* Tags row */}
          <div className="job-feed-card-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            {job.salary && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
                color: accentColor, fontWeight: 600, padding: '3px 8px',
                borderRadius: tokens.radius.pill,
                background: salaryTagBg,
                border: `1px solid ${salaryTagBorder}`,
              }}>
                <IndianRupee size={13} /> {job.salary}
              </span>
            )}
            {formatExperience(job.experience) && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
                color: muted, fontWeight: 500, padding: '3px 8px',
                borderRadius: tokens.radius.pill,
                background: tagBg,
                border: `1px solid ${border}`,
              }}>
                <Briefcase size={13} /> {formatExperience(job.experience)}
              </span>
            )}
            {job.employment_type && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
                color: muted, fontWeight: 500, padding: '3px 8px',
                borderRadius: tokens.radius.pill,
                background: tagBg,
                border: `1px solid ${border}`,
              }}>
                <Layers size={13} /> {job.employment_type}
              </span>
            )}
            {job.applicants_count != null && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
                color: muted, fontWeight: 500, padding: '3px 8px',
                borderRadius: tokens.radius.pill,
                background: tagBg,
                border: `1px solid ${border}`,
              }}>
                <Users size={13} /> {job.applicants_count} applicants
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="job-feed-card-footer" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 18 }}>
            <JobCardActions
              job={job}
              onApply={onApply}
              onFindContact={onFindContact}
              onViewDetails={onViewDetails}
              green={green}
              isDark={isDark}
              border={border}
              cardBg={cardBg}
              muted={muted}
              accentColor={accentColor}
            />
            {onToggleExpand && (
              <button
                type="button"
                onClick={() => onToggleExpand(key)}
                style={{ border: 'none', background: 'transparent', color: muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Why this matches {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </div>

          {/* Expanded reasons */}
          {isExpanded && whyReasons.length > 0 && (
            <div style={{ marginTop: 12, padding: 14, borderRadius: tokens.radius.lg, background: reasonsBg, border: `1px solid ${border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: dimText, marginBottom: 4 }}>WHY THIS COMPANY</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {whyReasons.map((r, i) => (
                  <li key={`${r.text}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, fontSize: 13, color: text }}>
                    <Check size={14} color={green} style={{ marginTop: 2, flexShrink: 0 }} />
                    {r.text}
                  </li>
                ))}
                {remainingCount > 0 && (
                  <li style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, fontSize: 12, color: muted }}>
                    <span style={{ width: 14, display: 'inline-block' }} />
                    + {remainingCount} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @media (max-width: 720px), (max-device-width: 720px) {
          .job-feed-card {
            border-radius: 18px !important;
            padding: 14px !important;
            margin-bottom: 12px !important;
          }
          .job-feed-card-layout {
            gap: 10px !important;
          }
          .job-feed-card-logo {
            width: 40px !important;
            height: 40px !important;
            border-radius: 12px !important;
            font-size: 13px !important;
          }
          .job-feed-card-head {
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .job-feed-card-copy h3 {
            font-size: 15px !important;
            overflow-wrap: anywhere;
          }
          .job-feed-card-tools {
            gap: 4px !important;
          }
          .job-feed-card-tools span {
            max-width: 96px;
          }
          .job-feed-card-tags {
            margin-top: 12px !important;
            gap: 6px !important;
          }
          .job-feed-card-footer {
            align-items: stretch !important;
            gap: 10px !important;
            margin-top: 14px !important;
          }
          .job-feed-card-actions {
            display: grid !important;
            grid-template-columns: minmax(92px, 0.92fr) minmax(122px, 1.15fr) minmax(88px, 0.9fr);
            gap: 8px !important;
            width: 100%;
          }
          .job-feed-card-actions > * {
            justify-content: center !important;
            min-height: 40px !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            white-space: nowrap;
          }
          .job-feed-card-footer > button {
            width: 100%;
            justify-content: flex-start !important;
            padding-top: 2px !important;
          }
        }
        @media (max-width: 390px) {
          .job-feed-card-actions {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </article>
  );
};

export default JobFeedCard;
